// import { useState } from 'react';
// import Sidebar from './Sidebar';

// const Layout = ({ children }) => {
//   const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

//   return (
//     <div style={{ display: 'flex', minHeight: '100vh', background: '#f8fafc' }}>
//       <Sidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} />
//       <div
//         style={{
//           flex: 1,
//           marginLeft: collapsed ? '80px' : '280px',
//           transition: 'margin-left 0.25s ease',
//           overflowY: 'auto',                 // ← prevents body scroll issues
//           background: '#f8fafc',
//           minHeight: '100vh',
//         }}
//       >
//         <main style={{ padding: '1.5rem 2rem' }}>   {/* ← better spacing */}
//           {children}
//         </main>
//       </div>
//     </div>
//   );
// };

// export default Layout;
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FiMenu } from 'react-icons/fi';
import Sidebar from './SideBar';
import DepartmentSwitcher from './DepartmentSwitcher';
import NotificationBell from './NotificationBell';
import useIsMobile from '../hooks/useIsMobile';
import useBodyScrollLock from '../hooks/useBodyScrollLock';
import { SIDEBAR_WIDTH } from '../theme/breakpoints';
import { COLORS } from '../constants/ui';

const COLLAPSE_KEY = 'sidebarCollapsed';

/**
 * Remember the rail's state across navigations.
 *
 * app.js wraps each route in its own <Layout>, so this component remounts on
 * every navigation — plain useState reset the user's choice each time they
 * moved. Persisting it is what makes the preference stick.
 */
function readCollapsed() {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false; // private mode / storage disabled — just default open
  }
}

const Layout = ({ children }) => {
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readCollapsed);
  // On a phone the rail is a drawer over the content rather than beside it,
  // so "open" is a separate idea from the desktop "collapsed".
  const [drawerOpen, setDrawerOpen] = useState(false);

  // The mobile drawer covers the page; the page behind it must not scroll.
  useBodyScrollLock(isMobile && drawerOpen);
  const { pathname } = useLocation();
  const isBudgetPage = pathname === '/budget' || pathname.startsWith('/budget/');

  const setCollapsed = useCallback((next) => {
    setSidebarCollapsed((prev) => {
      const value = typeof next === 'function' ? next(prev) : next;
      try {
        window.localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0');
      } catch {
        /* not worth failing the click over */
      }
      return value;
    });
  }, []);

  // Tapping a nav item should feel like it navigated, not like it opened a menu.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Escape closes the drawer, matching every other overlay in the app.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  // The content well sits beside the rail on desktop and under it on mobile.
  const contentOffset = isMobile
    ? 0
    : sidebarCollapsed
    ? SIDEBAR_WIDTH.collapsed
    : SIDEBAR_WIDTH.expanded;

  return (
    <div
      style={{
        display: 'flex',
        minHeight: '100vh',
        // dvh tracks Safari's collapsing URL bar; vh is the fallback and is
        // declared first so older browsers keep the value they understand.
        height: '100vh',
        maxHeight: '100dvh',
        overflow: 'hidden',
      }}
    >
      <Sidebar
        collapsed={isMobile ? false : sidebarCollapsed}
        setCollapsed={setCollapsed}
        isMobile={isMobile}
        drawerOpen={drawerOpen}
        onCloseDrawer={() => setDrawerOpen(false)}
      />

      {/* Backdrop — only on mobile, only while the drawer is out. */}
      {isMobile && drawerOpen ? (
        <div
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,0.5)',
            zIndex: 1150,
          }}
        />
      ) : null}

      <div
        data-app-main-scroll
        style={{
          flex: 1,
          minWidth: 0, // let children shrink instead of forcing the page wide
          minHeight: 0,
          marginLeft: contentOffset,
          transition: 'margin-left 0.3s ease',
          overflowY: 'auto',
          // The canvas every page sits on — Tracker's sunken tone, so cards
          // read as raised against it consistently across the app.
          background: COLORS.sunken,
        }}
      >
        {/* Mobile top bar — the only way to reach the nav once the rail is
            off-canvas. Header.js exists but was never mounted, so the trigger
            lives here with the shell it controls. On desktop the bell rides in
            the department bar instead, rather than reserving a whole strip. */}
        {isMobile ? (
          <div
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 900,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '0.6rem 1rem',
              background: COLORS.surface,
              borderBottom: `1px solid ${COLORS.line}`,
            }}
          >
            <button
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 38,
                height: 38,
                borderRadius: 10,
                border: `1px solid ${COLORS.line}`,
                background: '#fff',
                cursor: 'pointer',
                color: COLORS.ink,
                flexShrink: 0,
              }}
            >
              <FiMenu size={20} />
            </button>
            <span style={{ fontWeight: 800, color: COLORS.accentInk, fontSize: '0.95rem' }}>
              Team Space
            </span>
            <div style={{ flex: 1 }} />
            <NotificationBell />
          </div>
        ) : null}

        <main style={{ padding: isMobile ? '1rem' : '1.5rem 2rem' }}>
          {/* Budget updates in place from the departmentViewChanged event, so
              it opts out of the reload every other page relies on. */}
          <DepartmentSwitcher
            liveUpdate={isBudgetPage}
            trailing={!isMobile ? <NotificationBell /> : null}
          />
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;